/* eslint-disable @typescript-eslint/no-shadow */
import { Fragment, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty, isNonNullish } from 'remeda'
import { useMediaQuery } from 'usehooks-ts'
import { gql, type TypedDocumentNode, useQuery } from '@apollo/client'
import { useHeader } from '@velasca/hooks/use-header'
import type { Variant } from '@velasca/types'
import { SansText } from '@velasca/ui/sans-text'
import { SansTitle } from '@velasca/ui/sans-title'
import { SerifTitle } from '@velasca/ui/serif-title'
import { getVariantDot } from '@velasca/utils/get-variant-dot'
import { $settings } from '@velasca/utils/globals'
import { handleError } from '@velasca/utils/handle-error'
import { scrollTo } from '@velasca/utils/scroll-to'
import ColorSelector from '../ColorSelector'
import { useExchangeRates } from '../ExchangeRatesProvider'
import {
  Col,
  Details,
  Footer,
  Head,
  Hr,
  InfoBlock,
  InfoLinks,
  PriceCol,
  Row,
  Separator,
  StyledPairWith,
  StyledRelatedSoles,
  StyledReviewBadge,
  Variants,
} from './InfoblockHeader.styles'
import type {
  GetProductVariantsConnectionData,
  GetProductVariantsConnectionVariables,
  InfoBlockHeaderPresentationalProps,
  InfoBlockHeaderProps,
} from './InfoBlockHeader.types'

export const InfoBlockHeaderPresentational = ({
  collectionBrandName,
  loading,
  variants,
  selectedVariant,
  price,
  rawPrice,
  compareAtPrice,
  rawCompareAtPrice,
  description,
  reviews,
  title,
  footerLinks,
  pairWithText,
  pairWithTitle,
  productType,
  seasonName,
  soleDesc,
  handle,
  relatedSoles,
}: InfoBlockHeaderPresentationalProps) => {
  const headerHeight = useHeader(({ headerHeight }) => headerHeight)
  const isMinWidth370px = useMediaQuery('(min-width: 370px)')

  return (
    <InfoBlock>
      <Head>
        {variants && (
          <Variants>
            <ColorSelector
              showSkeleton
              variants={variants}
              loading={loading}
              currentVariant={selectedVariant}
            />
          </Variants>
        )}
        <div>
          {collectionBrandName && (
            <SansTitle fontWeight="500" color="primary" as="h2" size="xs">
              {collectionBrandName}
            </SansTitle>
          )}
          <Row>
            <Col>
              <SerifTitle
                as="h1"
                color="primary"
                size={isMinWidth370px ? 'lg' : 'base'}
              >
                {title}
              </SerifTitle>

              {reviews && (
                <StyledReviewBadge
                  starSize={20}
                  reviewsAverage={reviews ? reviews.average : 0}
                  reviewsCount={reviews?.count || 0}
                  onClick={() => {
                    scrollTo('#stamped-main-widget', headerHeight)
                  }}
                />
              )}
            </Col>
            <PriceCol>
              {price && (
                <SansText
                  as="span"
                  size="xl"
                  fontWeight="500"
                  dangerouslySetInnerHTML={{ __html: price }}
                />
              )}
              {compareAtPrice &&
                isNonNullish(rawCompareAtPrice) &&
                isNonNullish(rawPrice) &&
                rawCompareAtPrice > rawPrice && (
                  <SansText
                    strikethrough
                    as="span"
                    size="md"
                    color="gray"
                    dangerouslySetInnerHTML={{
                      __html: compareAtPrice,
                    }}
                  />
                )}
            </PriceCol>
          </Row>

          <Details>
            {productType && <SansText size="md">{productType}</SansText>}
            <SansText size="md">{seasonName}</SansText>
            {description?.color && (
              <SansText size="md">{description.color}</SansText>
            )}
            {description?.composition && (
              <SansText size="md">{description.composition}</SansText>
            )}
            {description?.html && (
              <SansText
                size="md"
                margin="1rem 0 0 0"
                dangerouslySetInnerHTML={{
                  __html: description.html,
                }}
              />
            )}
            {soleDesc && <SansText size="md">{soleDesc}</SansText>}
          </Details>
        </div>
      </Head>

      {relatedSoles && !isEmpty(relatedSoles) && handle && (
        <StyledRelatedSoles
          soles={relatedSoles}
          currentProductHandle={handle}
        />
      )}

      {pairWithText && (
        <>
          <Hr />
          <StyledPairWith title={pairWithTitle} paragraph={pairWithText} />
        </>
      )}

      {footerLinks && !isEmpty(footerLinks) && (
        <Footer>
          <Hr />
          <InfoLinks length={footerLinks?.length}>
            {footerLinks?.map(({ link, description }, index) => {
              return (
                <Fragment key={index}>
                  <SansText
                    fontStyle="italic"
                    color="primary"
                    size="md"
                    onClick={() => {
                      scrollTo(link, headerHeight)
                    }}
                  >
                    {description}
                  </SansText>
                  {index !== footerLinks.length - 1 && <Separator />}
                </Fragment>
              )
            })}
          </InfoLinks>
        </Footer>
      )}
      <Hr />
    </InfoBlock>
  )
}

const GET_PRODUCT_COLOR_VARIANTS: TypedDocumentNode<
  GetProductVariantsConnectionData,
  GetProductVariantsConnectionVariables
> = gql`
  query GetProductVariants($query: String) {
    products(first: 250, query: $query) {
      edges {
        node {
          id
          title
          handle
          variantColorStyle: metafield(
            namespace: "custom"
            key: "variant_color_style"
          ) {
            value
          }
          textureImageName: metafield(
            namespace: "custom"
            key: "texture_image_name"
          ) {
            value
          }
          colorDescription: metafield(namespace: "custom", key: "color") {
            value
          }
          materialShort: metafield(namespace: "custom", key: "material_short") {
            value
          }
          variantImage: metafield(
            namespace: "custom"
            key: "pattern_image_name"
          ) {
            value
          }
          variantColors: metafield(namespace: "custom", key: "variant_colors") {
            value
          }
          soleColorHex: metafield(namespace: "custom", key: "sole_color_hex") {
            value
          }
        }
      }
    }
  }
`

const InfoBlockHeader = ({
  collectionBrandName,
  description,
  footerLinks,
  handle,
  rawPrice,
  rawCompareAtPrice,
  reviews,
  title,
  pairWithProducts,
  productType,
  seasonName,
  soleDesc,
  relatedSoles,
}: InfoBlockHeaderProps): JSX.Element => {
  const { loading, data, error } = useQuery(GET_PRODUCT_COLOR_VARIANTS, {
    variables: {
      query: `title:${title}`,
    },
  })

  const { t } = useTranslation('product')

  const { convert } = useExchangeRates()

  useEffect(() => {
    if (!error) {
      return
    }

    handleError(error, {
      message: "Couldn't fetch product variants",
      behaviour: 'throw',
    })
  }, [error])

  const variants: Variant[] =
    data?.products?.edges
      .filter(({ node: { title: variantTitle } }) => variantTitle === title)
      .map(
        ({
          node: {
            id,
            title,
            handle,
            variantColorStyle,
            textureImageName,
            variantImage,
            variantColors,
            materialShort,
            colorDescription,
            soleColorHex,
          },
        }) => {
          return {
            id,
            title,
            handle,
            description: {
              bold: colorDescription?.value ?? null,
              thin: materialShort?.value ?? null,
            },
            variantDot: getVariantDot({
              variantColors,
              soleColorHex,
              variantColorStyle,
              textureImageName,
              variantImage,
              imageAssetPublicPath: $settings.imageAssetPublicPath,
            }),
          }
        },
      ) || []

  const selectedVariant = variants?.find(
    ({ handle: variantHandle }) => variantHandle === handle,
  )

  return (
    <InfoBlockHeaderPresentational
      collectionBrandName={collectionBrandName}
      loading={loading}
      variants={variants}
      selectedVariant={selectedVariant}
      price={rawPrice ? convert(rawPrice) : undefined}
      rawPrice={rawPrice}
      rawCompareAtPrice={rawCompareAtPrice}
      description={description}
      reviews={reviews}
      title={title}
      footerLinks={footerLinks}
      pairWithText={pairWithProducts && t('pairWithText', pairWithProducts)}
      pairWithTitle={t('pairWithTitle')}
      productType={productType}
      seasonName={seasonName}
      soleDesc={soleDesc}
      handle={handle}
      relatedSoles={relatedSoles}
      compareAtPrice={
        rawCompareAtPrice ? convert(rawCompareAtPrice) : undefined
      }
    />
  )
}

export default InfoBlockHeader
